<template>
  <section
    class="LoginPage"
    :class="{
      'LoginPage--isEmailSent': isEmailSent
    }"
  >
    <NativeBannerView />

    <div class="LoginPage__container">
      <div class="LoginPage__wrapper">
        <header class="LoginPage__header">
          <BallLogo class="LoginPage__logo" />
          <h1 class="LoginPage__title">
            <b>Ball</b>.app
          </h1>
          <p class="LoginPage__subtitle">
            Because ball is life.
          </p>
        </header>
        <Accordion
          class="LoginPage__oauthAccordion"
          :isExpanded="!isEmailSent"
        >
          <div
            class="LoginPage__oauthButtonList"
          >
            <FormButton
              class="LoginPage__oauthButton"
              logoName="google"
              backgroundColor="#000"
              textColor="#fff"
              :disabled="isRequesting"
              @click="handleGoogleClick"
            >
              Sign in with Google
            </FormButton>
            <FormButton
              class="LoginPage__oauthButton"
              logoName="apple"
              backgroundColor="#000"
              textColor="#fff"
              :disabled="isRequesting"
              @click="handleAppleClick"
            >
              Sign in with Apple
            </FormButton>
            <p>or</p>
          </div>
        </Accordion>
        <form
          class="LoginPage__form"
          @submit.prevent="handleSubmit"
        >
          <p
            v-if="isEmailSent"
            ref="formMessageRef"
            class="LoginPage__formMessage"
          >
            We emailed you a login code.<br>
            Check your inbox <em>(and spam folder)</em>
          </p>

          <fieldset
            class="LoginPage__fieldset"
            :disabled="isRequesting || isVerifyingToken || isCaptchaError"
          >
            <Transition>
              <div
                v-if="!isEmailSent"
                class="LoginPage__inputContainer"
              >
                <FormInput
                  v-model="email"
                  class="LoginPage__input LoginPage__input--email"
                  type="email"
                  name="email"
                  ariaLabel="email"
                  autocomplete="email"
                  :placeholder="emailPlaceholder"
                  is1PasswordEnabled
                  isRequired
                />
              </div>
              <div
                v-else
                class="LoginPage__inputContainer"
              >
                <FormInput
                  v-model="token"
                  class="LoginPage__input LoginPage__input--otp"
                  name="otp"
                  ariaLabel="one-time passcode"
                  autocomplete="off"
                  placeholder="000000"
                  inputMode="numeric"
                  required
                />
              </div>
            </Transition>
            <FormButton
              type="submit"
              :disabled="!isEmailSent && !captchaToken"
            >
              <span v-if="isEmailSent">Submit</span>
              <span v-else-if="isSendingEmail">Sending email…</span>
              <span v-else-if="isCaptchaError">Couldn’t verify you’re human</span>
              <span
                v-else
              >Sign in with email</span>
            </FormButton>
          </fieldset>
          <TurnstileWidget
            v-if="!isEmailSent"
            ref="turnstileRef"
            v-model="captchaToken"
            @error="handleCaptchaError"
          />
        </form>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import Accordion from "@/components/Accordion.vue";
import BallLogo from "@/components/BallLogo.vue";
import FormButton from "@/components/FormButton.vue";
import FormInput from "@/components/FormInput.vue";
import TurnstileWidget from "@/components/TurnstileWidget.vue";
import useAppTheme from "@/composables/useAppTheme";
import { supabaseClient } from "@/lib/database/supabase";
import { RouteName } from "@/lib/router/routes";
import getAppUrl from "@/lib/utils/http/getAppUrl";
import useAuthStore from "@/stores/useAuthStore";
import NativeBannerView from "@/views/NativeBannerView.vue";
import { topPlayerMap } from "@shared/lib/constants/players";
import { Theme } from "@shared/lib/constants/themes";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();
const route = useRoute();

const {
  signInWithEmail,
  signInWithGoogle,
  signInWithApple,
} = useAuthStore();

const {
  theme,
} = useAppTheme();

const formMessageRef = useTemplateRef("formMessageRef");
const formMessageHeight = ref(0);
const turnstileRef = useTemplateRef("turnstileRef");

const email = ref("");
const token = ref("");
const captchaToken = ref("");
const isCaptchaError = ref(false);

const emailPlaceholder = ref("");

const isEmailSent = ref(false);
const isSendingEmail = ref(false);
const isVerifyingToken = ref(false);
const isRedirecting = ref(false);

const isRequesting = computed(() => isSendingEmail.value || isRedirecting.value);

watch(captchaToken, () => {
  if (captchaToken.value) {
    isCaptchaError.value = false;
  }
});

function handleCaptchaError () {
  isCaptchaError.value = true;
}

onMounted(() => {
  if (route.query.error && route.query.error_description) {
    alert(route.query.error_description);
  }

  emailPlaceholder.value = topPlayerMap.get((theme.value as keyof typeof Theme | null) || "LosAngelesLakers")?.email || "lebron@lakers.com";
});

watch(formMessageRef, () => {
  formMessageHeight.value = formMessageRef.value?.getBoundingClientRect().height || 0;
});

const redirectRoute = computed(() => {
  const queryRoute = route.query.redirect
    ? router.resolve(decodeURIComponent(route.query.redirect.toString()))
    : null;

  return queryRoute && queryRoute.matched.length !== 0
    ? queryRoute
    : router.resolve({
      name: RouteName.home,
    });
});

const redirectUrl = computed(() => {
  return getAppUrl(redirectRoute.value.fullPath);
});

async function handleSubmit () {
  if (!isEmailSent.value) {
    handleSignInWithEmail();
  } else {
    handleSignInWithOtp();
  }
}

async function handleSignInWithEmail () {
  isSendingEmail.value = true;

  if (email.value.trim().toLowerCase() === "testflight@ball.app") {
    const {
      error,
    } = await supabaseClient.auth.signInWithPassword({
      email: "testflight@ball.app",
      password: "N2tNAL.YJB!jMfw@NmsLgw@Wzt6w3qMG",
      options: {
        captchaToken: captchaToken.value,
      },
    });

    if (!error) {
      router.push(redirectRoute.value);
    } else {
      alert(error.message);
    }
  } else {
    const {
      error,
    } = await signInWithEmail(email.value, {
      emailRedirectTo: redirectUrl.value,
      captchaToken: captchaToken.value,
    });

    if (!error) {
      isEmailSent.value = true;
    } else {
      alert(error.message);
    }
  }

  turnstileRef.value?.reset();
  isSendingEmail.value = false;
}

async function handleSignInWithOtp () {
  isVerifyingToken.value = true;
  const {
    error,
  } = await supabaseClient.auth.verifyOtp({
    type: "email",
    email: email.value,
    token: token.value,
  });
  isVerifyingToken.value = false;

  if (!error) {
    router.push(redirectRoute.value);
  } else {
    alert(error.message);
  }
}

async function handleGoogleClick () {
  isRedirecting.value = true;
  await signInWithGoogle({
    redirectTo: redirectUrl.value,
  });
  isRedirecting.value = false;
}

async function handleAppleClick () {
  isRedirecting.value = true;
  await signInWithApple({
    redirectTo: redirectUrl.value,
  });
  isRedirecting.value = false;
}
</script>

<style lang="scss" scoped>
.LoginPage {
  --transition: 350ms;

  display: flex;
  flex-direction: column;
  height: 100%;

  background-color: var(--themeColorPrimary);
  color: var(--themeColorNeutral0);

  &:has(.UpdateBannerView) {
    padding-top: var(--safeAreaInsetTop);
  }
}

.LoginPage__container {
  display: grid;
  flex-grow: 1;
  place-items: center;
}

.LoginPage__wrapper {
  isolation: isolate;
  display: grid;
  padding: 16px;
}

.LoginPage__header {
  display: grid;
  text-align: center;
}

.LoginPage__logo {
  --outlineColor: currentColor;
  margin: 0 auto 16px;
}

.LoginPage__title {
  font: var(--fontWeightLight) 40px / normal var(--fontFamilyNormal);

  b {
    font-weight: var(--fontWeightMedium);
    font-stretch: var(--fontWidthExtended);
  }
}

.LoginPage__subtitle {
  margin-top: 8px;
  font-size: 16px;
  font-stretch: var(--fontWidthExtended);
}

.LoginPage__form {
  position: relative;
  margin: 24px 0;
}

.LoginPage__formMessage {
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  padding-bottom: 24px;

  font-stretch: var(--fontWidthExtended);
  text-align: center;
  line-height: 1.5;

  transition: opacity var(--transition);

  &.v-enter-from {
    opacity: 0;
  }
}

.LoginPage__fieldset {
  position: relative;

  display: grid;
  gap: 12px;
  min-width: 320px;

  transition: transform var(--transition);

  .LoginPage--isEmailSent & {
    transform: translateY(calc(1px * v-bind(formMessageHeight)));
  }
}

.LoginPage__inputContainer {
  transition: opacity var(--transition);

  &.v-leave-active {
    position: absolute;
    left: 0;
    width: 100%;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.LoginPage__input--otp {
  text-align: center;
}

.LoginPage__oauthAccordion {
  --transitionDuration: 350ms;
}

.LoginPage__oauthButtonList {
  position: relative;

  display: grid;
  gap: 12px;
  justify-items: center;
  margin-top: 48px;

  font-stretch: var(--fontWidthExtended);
  text-align: center;

  transition: opacity var(--transition);

  .LoginPage--isEmailSent & {
    opacity: 0;
    pointer-events: none;
  }

  :deep(p) {
    margin-top: 12px;
  }
}

.LoginPage__oauthButton {
  --backgroundColorDisabled: var(--themeDarkTransparent400);
  --textColorDisabled: var(--themeColorNeutral300);

  &:disabled {
    --fillColor: var(--themeColorNeutral500);
  }
}
</style>
